/**
 * Legal content for the public /privacy and /terms pages and the client consent screen.
 *
 * Keep this the single source of truth so the consent screen, the footer links and the
 * standalone pages always show the same wording. Have any change reviewed before release,
 * and update LAST_UPDATED when the wording changes.
 */

export const LEGAL_ENTITY = {
  // Talk Australia is a registered business name of IT & T Communications Pty Ltd (ABN Lookup, checked 31 Oct 2026)
  name: 'IT & T Communications Pty Ltd, trading as Talk Australia',
  shortName: 'IT & T Communications Pty Ltd',
  abn: '81 161 895 272',
  location: 'New South Wales',
  address: 'Level 5 Nexus Norwest, 4 Columbia Ct, Norwest NSW 2153',
  phone: '02 4056 1446',
  privacyEmail: 'support@talkaustralia.com.au',
};

export const LAST_UPDATED = '31 October 2026';

export const TERMS_SECTIONS = [
  {
    heading: '1. Service Agreement',
    body: `By using the Talk Australia Client Portal, you agree to these Terms of Service. ${LEGAL_ENTITY.name} (ABN ${LEGAL_ENTITY.abn}) provides IT and telecommunications services including but not limited to: internet services, phone systems, cybersecurity, managed IT, and cloud solutions.`,
  },
  {
    heading: '2. Account Responsibilities',
    body: 'You are responsible for maintaining the confidentiality of your login credentials. You agree to notify Talk Australia immediately of any unauthorized access to your account.',
  },
  {
    heading: '3. Service Level Agreements',
    body: 'Service levels vary by tier (SMB, Mid-Market, Enterprise). Your specific SLA is detailed in your service contract. Enterprise clients receive 24/7 priority support with guaranteed response times.',
  },
  {
    heading: '4. Data Usage',
    body: 'Data collected through this portal is used to provide and improve services, as described in our Privacy Policy. We comply with the Australian Privacy Act 1988 and never sell your personal information to third parties.',
  },
  {
    heading: '5. Acceptable Use',
    body: "You agree not to use the portal for any unlawful purpose, attempt to gain unauthorized access to systems, or interfere with the portal's operation.",
  },
  {
    heading: '6. Dispute Resolution',
    body: 'Disputes will be resolved under the laws of New South Wales, Australia. We encourage resolution through our support channels before formal proceedings.',
  },
];

export const PRIVACY_SECTIONS = [
  {
    heading: 'Who we are',
    body: `${LEGAL_ENTITY.name} (ABN ${LEGAL_ENTITY.abn}) is a family-owned IT, telecommunications and technology provider based in ${LEGAL_ENTITY.location}. This policy explains how we handle personal information under the Privacy Act 1988 (Cth) and the Australian Privacy Principles. Where they apply, we also follow Part 13 of the Telecommunications Act 1997 and the Telecommunications Consumer Protections Code.`,
  },
  {
    heading: 'What we collect',
    body: 'Contact and business details (name, job title, email, phone, company and ABN); account and login details; service, billing, contract, ticket and payment records; messages you send us through the portal; e-signature details when you accept a proposal (name, email, time, and a fingerprint of the signed document); and technical details such as your IP address and browser type. If you switch them on, we also collect the optional device, network and usage details described on the portal consent screen.',
  },
  {
    heading: 'Proposal pages',
    body: 'When we send you a proposal link, the page records when it is opened, which sections are viewed, how long they are viewed and how far you scroll, along with your IP address and browser type. We use this so your account manager knows when to follow up and which parts of the proposal to explain. We keep these viewing records for a limited period and do not sell them or use them for advertising.',
  },
  {
    heading: 'Meetings, calls and AI tools',
    body: 'With your agreement, we may record or take automated notes of sales and support meetings, and use AI tools to summarise them and to help prepare proposals that address the issues you raised. We tell you before a meeting is recorded. Our staff review AI-assisted content before it is sent to you. We do not enter sensitive information into publicly available AI tools, and we choose AI providers whose terms do not allow them to train their models on your information.',
  },
  {
    heading: 'Automated decisions',
    body: 'We do not use computer programs to make decisions that significantly affect your rights or interests without a person involved. If that changes, we will update this policy to describe the kinds of personal information used and the kinds of decisions made.',
  },
  {
    heading: 'How we use and share information',
    body: 'We use personal information to prepare proposals, provide and support services, bill you, communicate with you, keep our systems secure, and meet legal obligations. We share it only with service providers who help us do this (for example hosting, database, email delivery, payment processing and meeting-notes providers), with carriers and suppliers needed to deliver your services, or where the law requires.',
  },
  {
    heading: 'Where your information is stored',
    body: 'Your information is stored on our servers in Australia. Some service providers we use to deliver email, analytics or meeting notes may process information in the United States. We take reasonable steps to make sure they protect it in line with the Australian Privacy Principles.',
  },
  {
    heading: 'Marketing',
    body: 'We only send marketing messages if you have agreed or would reasonably expect them, and every marketing email includes a way to unsubscribe. You can also switch marketing off on the portal consent screen.',
  },
  {
    heading: 'Security and retention',
    body: 'We protect information with access controls, encryption in transit, audit logging and regular security updates. We keep information only as long as we need it for the purposes above or as the law requires, and then delete or de-identify it.',
  },
  {
    heading: 'Access, correction and complaints',
    body: `You can ask to access or correct the personal information we hold about you, or ask us to delete information we no longer need, by emailing ${LEGAL_ENTITY.privacyEmail}, calling ${LEGAL_ENTITY.phone}, or writing to us at ${LEGAL_ENTITY.address}. We aim to respond within 30 days. If you are not satisfied with how we handle a privacy complaint, you can contact the Office of the Australian Information Commissioner at oaic.gov.au or on 1300 363 992.`,
  },
];
